import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} StudentQuizLimitInfo
 * @property {number} student_id - The student's ID
 * @property {string} name - The student's name
 * @property {string} username - The student's username
 * @property {number|null} limit - The quiz attempt limit, or null if not set
 * @property {number} used_attempts - Number of attempts used
 * @property {number|null} remaining_attempts - Number of remaining attempts, or null if unlimited
 * @property {number|null} attempt_usage_percent - Percentage of attempts used, or null if unlimited
 * @property {boolean} addon_available - Whether addon attempts are available
 */

/**
 * @typedef {import("../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @description Custom hook to fetch quiz limit info for students in a classroom
 */
export const useGetClassroomQuizLimitInfo = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [students, setStudents] = useState(
        /** @type {StudentQuizLimitInfo[] | null | undefined} */(undefined),
    );
    const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));

    const { axiosInstance } = useAxios();

    /**
     * Fetch quiz limit info for a specific classroom
     * @async
     * @param {Object} params
     * @param {number|string} params.classroomId - The classroom ID
     * @param {number} [params.exhausted] - Filter for exhausted limits (1 for true, 0 for false)
     * @param {number} [params.page] - Page number for pagination
     * @param {number} [params.per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getClassroomQuizLimitInfo = async ({
        classroomId,
        exhausted,
        page = 1,
        per_page = 10,
    }) => {
        try {
            setLoading(true);
            setError("");

            const query = new URLSearchParams();
            query.append("page", page);
            query.append("per_page", per_page);
            if (exhausted !== undefined && exhausted !== null) {
                query.append("exhausted", exhausted);
            }

            const url = `${TENANT_DOMAIN}/quiz/limit/classroom/${classroomId}/show?${query.toString()}`;
            const response = await axiosInstance.get(url);

            setStudents(response?.data?.data || []);
            setMeta(response?.data?.meta || null);
        } catch (err) {
            setStudents(null);
            setMeta(null);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        students,
        meta,
        loading,
        error,
        getClassroomQuizLimitInfo,
    };
};